import { useNavigate } from "react-router-dom";
import { infantStories } from "@/mocks/infantDonations";

const InfantDonationSection = () => {
  const navigate = useNavigate();
  const featured = infantStories.filter((i) => !i.funded).slice(0, 3);

  return (
    <section className="py-20 bg-gradient-to-b from-rose-50 to-white relative overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-rose-100 rounded-full opacity-40 -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-rose-100 rounded-full opacity-30 translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">👶</span>
              <span className="text-rose-500 font-bold text-sm tracking-wide uppercase">Lottery Winners &amp; Generous Hearts Welcome</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Give Love, Milk &amp; Warmth to
              <br />
              <span className="text-rose-500">World Infants</span>
            </h2>
            <p className="text-gray-500 mt-3 text-base max-w-xl">
              A small donation provides formula milk, warm clothes &amp; nutrition to babies in need.
              <strong className="text-gray-700"> You receive a handmade gift, receipt &amp; photo</strong> from the infant&apos;s family.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={() => navigate("/infant-donation")}
              className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white font-bold px-6 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-heart-fill"></i>
              Donate Now
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* How it works — quick strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: "ri-heart-3-line", label: "Choose an Infant", color: "text-rose-500 bg-rose-50" },
            { icon: "ri-secure-payment-line", label: "Make a Donation", color: "text-amber-500 bg-amber-50" },
            { icon: "ri-gift-2-line", label: "Receive Family Gift", color: "text-rose-500 bg-rose-50" },
            { icon: "ri-image-2-line", label: "Get Photo & Receipt", color: "text-amber-500 bg-amber-50" },
          ].map((step, i) => (
            <div key={i} className="bg-white border border-rose-100 rounded-2xl px-4 py-4 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${step.color}`}>
                <i className={`${step.icon} text-lg`}></i>
              </div>
              <div>
                <div className="text-xs text-gray-400 font-semibold">Step {i + 1}</div>
                <div className="font-bold text-gray-800 text-sm leading-tight">{step.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Featured Infant Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {featured.map((infant) => (
            <div key={infant.id} className="bg-white border border-rose-100 rounded-3xl overflow-hidden hover:-translate-y-1 transition-transform duration-300">
              <div className="relative h-48 overflow-hidden">
                <img src={infant.image} alt={infant.infantName} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="bg-white/90 text-rose-600 text-xs font-bold px-2.5 py-1 rounded-full">{infant.flag} {infant.country}</span>
                  <span className="bg-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">{infant.need}</span>
                </div>
                <div className="absolute bottom-3 left-3">
                  <div className="text-white font-black text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>{infant.infantName}</div>
                  <div className="text-white/80 text-xs">{infant.age} old</div>
                </div>
              </div>
              <div className="p-5">
                <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">{infant.story}</p>
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                    <span className="font-semibold text-rose-500">${infant.amountRaised} raised</span>
                    <span>Goal: ${infant.amountNeeded}</span>
                  </div>
                  <div className="h-2 bg-rose-100 rounded-full overflow-hidden">
                    <div className="h-full bg-rose-500 rounded-full" style={{ width: `${Math.min(100, (infant.amountRaised / infant.amountNeeded) * 100)}%` }}></div>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/infant-donation")}
                  className="w-full bg-rose-500 hover:bg-rose-600 text-white font-bold py-2.5 rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2 text-sm"
                >
                  <i className="ri-heart-fill"></i>
                  Donate to {infant.infantName}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-rose-500 to-rose-600 rounded-3xl px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-black text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Luck Can Be Their Life
            </h3>
            <p className="text-rose-100 text-sm max-w-md">
              Lottery winners, entrepreneurs &amp; generous hearts — share a small blessing. Receive a handmade gift &amp; photo from the baby&apos;s family.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={() => navigate("/infant-donation")}
              className="bg-white text-rose-600 font-bold px-6 py-3 rounded-xl hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-heart-fill"></i>
              See All Infants
            </button>
            <button
              onClick={() => navigate("/infant-donation")}
              className="border-2 border-white/60 text-white font-bold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-question-line"></i>
              How It Works
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InfantDonationSection;
